import React from 'react'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { RecentTime, UtilisationPeriod } from '../../../../back-end/common/billyDashboard'
import DashboardCard, { HeaderBr } from './dashboardCard'
import { Loading } from '../ui/Loading/Loading'
import { Col, Row } from 'react-bootstrap'
import { minutesToHours, properCase } from '../../utilities/format'
import { Client, Time } from '../../../../back-end/utilities/apiDefinitions'
import { TestingStaff } from '../../screens/dashboard/dashboardWrapper'

interface ClientRow {
	clientXpmID: string
	clientName: string
	current: number
	previous: number
}

interface HoursByClientProps {
	period: UtilisationPeriod
	testingStaff?: TestingStaff
}

const timeReduce = (accMinutes: number, time: Time): number => (accMinutes += time.minutes)

const timeByClient = (recentTime: RecentTime | null, clients: Client[] | null): ClientRow[] => {
	if (!recentTime || !clients) {
		return []
	}

	const clientRows: ClientRow[] = clients.map((client) => {
		return {
			clientXpmID: client.xpmID,
			clientName: client.name,
			current: recentTime.currentPeriod.filter((t) => t.job.clientXpmID === client.xpmID).reduce(timeReduce, 0),
			previous: recentTime.previousPeriod.filter((t) => t.job.clientXpmID === client.xpmID).reduce(timeReduce, 0),
		}
	})

	return clientRows.filter((c) => c.current > 0 || c.previous > 0).sort((a, b) => b.current - a.current)
}

const HoursByClient = (props: HoursByClientProps) => {
	const { appState } = React.useContext(AppContext)

	const [recentTime, setRecentTime] = React.useState<RecentTime | null>(null)
	const [clients, setClients] = React.useState<Client[] | null>(null)
	const [errorMsg, setErrorMsg] = React.useState<string | null>(null)

	React.useEffect(() => {
		const getData = async () => {
			const testingStaffXpmID = props.testingStaff ? '/' + props.testingStaff.xpmID : ''
			const [recentTimeRes] = await Promise.all([
				Request.get(`billydashboard/times${testingStaffXpmID}?period=${props.period}`),
				Request.getPagedData(`client`, 4000, (data) => setClients((clients) => (clients || []).concat((data as { clients: Client[] }).clients))),
			])

			setRecentTime(recentTimeRes.data.recentTime)

			if (!recentTimeRes.data.success) {
				setErrorMsg(recentTimeRes.data.message)
			}
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState, props])

	const clientRows = timeByClient(recentTime, clients)

	if (errorMsg) {
		return (
			<DashboardCard background="cornflower">
				<div className="center-flex p-3">
					<span className="span-body-2 span-black">{errorMsg}</span>
				</div>
			</DashboardCard>
		)
	}

	if (!recentTime || !clients) {
		return (
			<DashboardCard background="cornflower">
				<div className="center-flex">
					<Loading show />
				</div>
			</DashboardCard>
		)
	}

	const colWidths = {
		name: 4,
		current: 4,
		prev: 4,
	}

	return (
		<DashboardCard background="cornflower">
			<Row className="pb-2">
				<Col sm={colWidths.name}>
					<span className="span-body-2 span-bold span-black">Hours by Client</span>
				</Col>
				<Col sm={colWidths.current}>
					<span className="span-body-2 span-bold span-black">Current {properCase(props.period)}</span>
				</Col>
				<Col sm={colWidths.prev}>
					<span className="span-body-2 span-bold span-black">Last {properCase(props.period)}</span>
				</Col>
			</Row>
			<HeaderBr color="black" />
			{clientRows.map((clientRow, index) => (
				<Row key={index} className="py-2">
					<Col sm={colWidths.name}>
						<span className="span-body-2 span-black">{clientRow.clientName}</span>
					</Col>
					<Col sm={colWidths.current}>
						<span className="span-body-2 span-black">{minutesToHours(clientRow.current)}</span>
					</Col>
					<Col sm={colWidths.prev}>
						<span className="span-body-2 span-black">{minutesToHours(clientRow.previous)}</span>
					</Col>
				</Row>
			))}
		</DashboardCard>
	)
}

export default HoursByClient
